import {
  GetAllPostsByAppVariantAndSelectedGeoAreasForbiddenOrganizationTagsEnum,
  GetAllPostsByAppVariantAndSelectedGeoAreasGroupPostFilterEnum,
  GetAllPostsByAppVariantAndSelectedGeoAreasTypesEnum,
  GetMeinOrtPostsSortByEnum,
  GetMeinOrtPostsTypesEnum,
  type GetAllPostsByAppVariantAndSelectedGeoAreasRequest,
  type GetAllPostsByAppVariantAndSelectedGeoAreasRequiredOrganizationTagsEnum,
  type GetAllPostsByAppVariantAndSelectedGeoAreasSortByEnum,
  type GetMeinOrtPostsLocalNewsPostFilterEnum,
  type GetMeinOrtPostsRequest
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { useMemo } from 'react';

import {
  LAST_100_DAYS,
  SIZE,
  TODAY_AS_NUMBER
} from '@/features/posts/constants/params';
import { usePostSortStore } from '@/features/posts/store/postSortStore';
import { PostFeedType } from '@/features/posts/types/postFeed';
import { HappeningSortEnum } from '@/features/posts/types/postSort';

export const getTypesForFeedType = (
  postType: PostFeedType
): Set<GetAllPostsByAppVariantAndSelectedGeoAreasTypesEnum> | undefined => {
  switch (postType) {
    case PostFeedType.GOSSIP: {
      return new Set([
        GetAllPostsByAppVariantAndSelectedGeoAreasTypesEnum.Gossip
      ]);
    }
    case PostFeedType.HAPPENING: {
      return new Set([
        GetAllPostsByAppVariantAndSelectedGeoAreasTypesEnum.Happening
      ]);
    }
    case PostFeedType.NEWS_ITEM: {
      return new Set([
        GetAllPostsByAppVariantAndSelectedGeoAreasTypesEnum.NewsItem
      ]);
    }
    case PostFeedType.MARKET: {
      return new Set([
        GetAllPostsByAppVariantAndSelectedGeoAreasTypesEnum.Offer,
        GetAllPostsByAppVariantAndSelectedGeoAreasTypesEnum.Seeking
      ]);
    }
    case PostFeedType.SUGGESTION: {
      return new Set([
        GetAllPostsByAppVariantAndSelectedGeoAreasTypesEnum.Suggestion
      ]);
    }
    case PostFeedType.SPECIAL_POST: {
      return new Set([
        GetAllPostsByAppVariantAndSelectedGeoAreasTypesEnum.SpecialPost
      ]);
    }
    case PostFeedType.CIVIL_PROTECTION: {
      return new Set([
        GetAllPostsByAppVariantAndSelectedGeoAreasTypesEnum.NewsItem
      ]);
    }
    case PostFeedType.EMERGENCY: {
      return new Set([
        GetAllPostsByAppVariantAndSelectedGeoAreasTypesEnum.NewsItem
      ]);
    }
    default: {
      return;
    }
  }
};

export type GetPostsParamsOptions = {
  /**
   * Restrict results to the explicitly selected (leaf) geo areas, excluding
   * ancestors auto-added to the selection ("Mein Ort" scope). Defaults to
   * true; set to false only for region-wide feeds that deliberately include
   * posts targeted at ancestor areas (e.g. "Meine Region").
   */
  onlyExplicitlySelectedGeoAreas?: boolean;
  requiredOrganizationTags?: GetAllPostsByAppVariantAndSelectedGeoAreasRequiredOrganizationTagsEnum[];
  /**
   * Replaces the default forbidden tags (CivilProtection, plus EmergencyAid
   * for news feeds). Use to exempt a feed from those exclusions, e.g. the
   * global Katwarn feed which requires the CivilProtection tag.
   */
  forbiddenOrganizationTags?: GetAllPostsByAppVariantAndSelectedGeoAreasForbiddenOrganizationTagsEnum[];
  /**
   * Asks the backend to exclude only the posts of the territorial authority
   * organization of the requesting user's home area (resolved server-side
   * from the access token). Other municipalities' territorial authorities
   * within the geo area selection remain visible. Has no effect when the home
   * area has no territorial authority organization.
   *
   * Used by the "Aus der Gemeinde" news filter (see useMeinOrtNewsFeed).
   */
  excludeHomeTerritorialAuthority?: boolean;
  /**
   * Overrides the sort order. Defaults to the user-selected sort store
   * (HappeningStart for happenings); pass a fixed value for feeds whose
   * order must not follow the user's sort preference (e.g. Katwarn).
   */
  sortBy?: GetAllPostsByAppVariantAndSelectedGeoAreasSortByEnum;
};

const getDefaultForbiddenOrganizationTags = (
  postType: PostFeedType
): GetAllPostsByAppVariantAndSelectedGeoAreasForbiddenOrganizationTagsEnum[] => {
  const CIVIL_PROTECTION =
    GetAllPostsByAppVariantAndSelectedGeoAreasForbiddenOrganizationTagsEnum.CivilProtection;
  const EMERGENCY_AID =
    GetAllPostsByAppVariantAndSelectedGeoAreasForbiddenOrganizationTagsEnum.EmergencyAid;
  const MINISTRY =
    GetAllPostsByAppVariantAndSelectedGeoAreasForbiddenOrganizationTagsEnum.Ministry;
  const PARTNER =
    GetAllPostsByAppVariantAndSelectedGeoAreasForbiddenOrganizationTagsEnum.Partner;
  const ENTERTAINMENT =
    GetAllPostsByAppVariantAndSelectedGeoAreasForbiddenOrganizationTagsEnum.Entertainment;
  // Partner posts (ENTERTAINMENT shares their distribution) are never in
  // "Mein Ort" (Verteilungslogik B4) — they surface on the MeineRegion feeds.
  const REGION_ONLY_ORG_TAGS = [PARTNER, ENTERTAINMENT];

  // Katwarn requires the CivilProtection tag, so nothing is forbidden there.
  if (postType === PostFeedType.CIVIL_PROTECTION) {
    return [];
  }

  if (postType === PostFeedType.EMERGENCY) {
    return [CIVIL_PROTECTION];
  }

  if (postType === PostFeedType.NEWS_ITEM) {
    return [CIVIL_PROTECTION, EMERGENCY_AID, MINISTRY, ...REGION_ONLY_ORG_TAGS];
  }

  // MeinOrt-local feeds (events/plausch/markt/alle). Ministry content
  // surfaces exclusively in the "Ministerien" tab of Meine Region — never
  // in Mein Ort.
  return [CIVIL_PROTECTION, MINISTRY, ...REGION_ONLY_ORG_TAGS];
};

export const useGetPostsParams = (
  postType: PostFeedType,
  options?: GetPostsParamsOptions
): GetAllPostsByAppVariantAndSelectedGeoAreasRequest => {
  const sort = usePostSortStore((state) => state.sort);

  // "Mein Ort" scope (explicitly selected leaf areas only) is the safe
  // default; region feeds opt out explicitly.
  const onlyExplicitlySelectedGeoAreas =
    options?.onlyExplicitlySelectedGeoAreas ?? true;

  const isHappening = postType === PostFeedType.HAPPENING;

  const requiredOrganizationTags: GetAllPostsByAppVariantAndSelectedGeoAreasRequiredOrganizationTagsEnum[] =
    options?.requiredOrganizationTags ?? [];
  const forbiddenOrganizationTags:
    | GetAllPostsByAppVariantAndSelectedGeoAreasForbiddenOrganizationTagsEnum[]
    | undefined =
    options?.forbiddenOrganizationTags ??
    getDefaultForbiddenOrganizationTags(postType);

  const excludeHomeTerritorialAuthority =
    options?.excludeHomeTerritorialAuthority ?? false;

  const types = useMemo(() => getTypesForFeedType(postType), [postType]);

  return useMemo(() => {
    const request: GetAllPostsByAppVariantAndSelectedGeoAreasRequest = {
      types,
      forbiddenOrganizationTags,
      requiredOrganizationTags,
      groupPostFilter:
        postType === PostFeedType.ALL
          ? GetAllPostsByAppVariantAndSelectedGeoAreasGroupPostFilterEnum.AllPosts
          : GetAllPostsByAppVariantAndSelectedGeoAreasGroupPostFilterEnum.ExcludeGroupPosts,
      count: SIZE,
      startTime: isHappening ? TODAY_AS_NUMBER : LAST_100_DAYS,
      // Happenings are always sorted by start time (next upcoming first).
      // A user-selectable happening sort might be re-added later.
      sortBy:
        options?.sortBy ??
        (isHappening ? HappeningSortEnum.HappeningStart : sort)
    };

    if (onlyExplicitlySelectedGeoAreas) {
      request.onlyExplicitlySelectedGeoAreas = true;
    }

    if (excludeHomeTerritorialAuthority) {
      request.excludeHomeTerritorialAuthority = true;
    }

    return request;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    types,
    postType,
    isHappening,
    sort,
    onlyExplicitlySelectedGeoAreas,
    excludeHomeTerritorialAuthority,
    options?.requiredOrganizationTags,
    options?.forbiddenOrganizationTags,
    options?.sortBy
  ]);
};

const getMeinOrtTypesForFeedType = (
  postType: PostFeedType
): Set<GetMeinOrtPostsTypesEnum> | undefined => {
  switch (postType) {
    case PostFeedType.GOSSIP: {
      return new Set([GetMeinOrtPostsTypesEnum.Gossip]);
    }
    case PostFeedType.HAPPENING: {
      return new Set([GetMeinOrtPostsTypesEnum.Happening]);
    }
    case PostFeedType.NEWS_ITEM: {
      return new Set([GetMeinOrtPostsTypesEnum.NewsItem]);
    }
    case PostFeedType.MARKET: {
      return new Set([
        GetMeinOrtPostsTypesEnum.Offer,
        GetMeinOrtPostsTypesEnum.Seeking
      ]);
    }
    case PostFeedType.SUGGESTION: {
      return new Set([GetMeinOrtPostsTypesEnum.Suggestion]);
    }
    case PostFeedType.SPECIAL_POST: {
      return new Set([GetMeinOrtPostsTypesEnum.SpecialPost]);
    }
    default: {
      return;
    }
  }
};

export const useGetMeinOrtPostsParams = (
  postType: PostFeedType,
  options?: Pick<GetPostsParamsOptions, 'sortBy'> & {
    localNewsPostFilter?: GetMeinOrtPostsLocalNewsPostFilterEnum;
  }
): GetMeinOrtPostsRequest => {
  const sort = usePostSortStore((state) => state.sort);
  const isHappening = postType === PostFeedType.HAPPENING;
  const types = useMemo(() => getMeinOrtTypesForFeedType(postType), [postType]);

  return useMemo(
    () => ({
      types,
      localNewsPostFilter: options?.localNewsPostFilter,
      count: SIZE,
      startTime: isHappening ? TODAY_AS_NUMBER : LAST_100_DAYS,
      sortBy:
        options?.sortBy ??
        (isHappening ? GetMeinOrtPostsSortByEnum.HappeningStart : sort)
    }),
    [types, isHappening, sort, options?.sortBy, options?.localNewsPostFilter]
  );
};
